/**
 * Picking a daily table back up after a reload (DLY-004, owner 2026-09-25: "resume
 * where they were").
 *
 * The saved record holds shots, not positions. Replaying them through the same
 * deterministic physics puts every ball back exactly where it was, with the same stroke
 * count, so a refresh is never a fresh attempt (D-029).
 */
import { PHYSICS_VERSION, type ShotInput, type ShotOutcome } from '@potted/sim';
import type { Session } from '../game/session.ts';
import type { DayRecord } from './history.ts';
import type { GameTable } from './table.ts';

/**
 * The saved record for this daily table, if it can be replayed here. A record from
 * other physics would replay differently (N10), and one whose par or ball count
 * disagrees with the level is not this table's: both are ignored rather than trusted.
 */
export function usableRecord(
  record: DayRecord | undefined,
  table: GameTable,
): DayRecord | undefined {
  if (record === undefined || table.mode !== 'daily') return undefined;
  if (record.dateKey !== table.id) return undefined;
  if (record.physicsVersion !== PHYSICS_VERSION) return undefined;
  if (record.par !== table.par || record.ballCount !== table.ballCount) return undefined;
  return record;
}

/**
 * GEN-011: today's attempt on this device is for a table that has since CHANGED (the
 * owner regenerated a published day). Set aside, like any record that does not fit, and
 * the send queue's count of it must go too: otherwise the queue believes the server
 * already has the new attempt's first shots, and never sends them.
 */
export function playedOnAnotherTable(record: DayRecord | undefined, table: GameTable): boolean {
  return (
    record !== undefined &&
    table.mode === 'daily' &&
    record.dateKey === table.id &&
    usableRecord(record, table) === undefined
  );
}

/**
 * Play `shots` into a fresh session, instantly: no animation, just each shot taken and
 * settled in order. Returns each shot's outcome, for the HUD and the shot colours.
 * Throws if the shots do not fit the table, such as a shot after it was cleared.
 */
export function replayInto(session: Session, shots: readonly ShotInput[]): ShotOutcome[] {
  const outcomes: ShotOutcome[] = [];
  for (const shot of shots) {
    if (session.phase() !== 'aiming') {
      throw new Error(
        `replayInto: shot ${String(outcomes.length + 1)} came after the table was ${session.phase()}`,
      );
    }
    session.take(shot);
    outcomes.push(session.settle());
  }
  return outcomes;
}
